import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { useTheme, alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Autocomplete, TextField, CircularProgress, Typography } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { bgBlur } from 'src/theme/css';
import { useOrganization } from 'src/contexts/OrganizationContext';

import Iconify from 'src/components/iconify';

import Searchbar from './common/searchbar';
import { NAV, HEADER } from './config-layout';
import AccountPopover from './common/account-popover';
import NotificationsPopover from './common/notifications-popover';

// ----------------------------------------------------------------------

export default function Header({ onOpenNav }) {
  const theme = useTheme();
  const { 
    organizations, 
    currentOrganization, 
    setCurrentOrganization,
    searchOrganizations,
    loading,
  } = useOrganization();
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    // This effect syncs the text input with the selected organization.
    // It ensures that when an organization is selected (or loaded from localStorage),
    // its name is displayed in the search box.
    if (currentOrganization) {
      setInputValue(currentOrganization.name);
    } else {
      setInputValue('');
    }
  }, [currentOrganization]);

  const lgUp = useResponsive('up', 'lg');
  const navigate = useNavigate();

  const handleSearch = (query) => {
    navigate(`/host-stats?search=${encodeURIComponent(query)}`);
  };

  const renderContent = (
    <>
      {!lgUp && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1 }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      <Searchbar onSearch={handleSearch} />

      <Box sx={{ flexGrow: 1 }} />
      
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: 320, mr: 2 }}>
        <Autocomplete
            fullWidth
            options={organizations}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option.name || ''}
            value={currentOrganization}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                searchOrganizations(newInputValue);
            }}
            onChange={(event, newValue) => {
                setCurrentOrganization(newValue);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loading}
            renderInput={(params) => (
                <TextField 
                    {...params} 
                    placeholder="Search Org or Domain..."
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Iconify 
                            icon="eva:search-fill" 
                            sx={{ ml: 1, mr: 0.5, color: 'text.disabled' }} 
                          />
                        ),
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const matchReason = option.targets && option.targets.length > 0 
                    ? `Found via domain: ${option.targets[0].name}`
                    : '';

                return (
                    <li {...props}>
                        <Iconify icon="mdi:domain" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            {matchReason && (
                                <Typography variant="caption" color="text.secondary">
                                    {matchReason}
                                </Typography>
                            )}
                        </Box>
                    </li>
                );
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
                '&.Mui-focused': {
                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              }
            }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <NotificationsPopover />
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.WIDTH + 1}px)`,
          height: HEADER.H_DESKTOP,
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  onOpenNav: PropTypes.func,
};